// Typed browser representations of the reviewed E03 and E04 contracts. The server remains
// authoritative for super-admin checks and entitlement decisions.

export const CATALOGUE_KINDS = ["product", "service", "module", "campaign"] as const;
export type CatalogueKind = (typeof CATALOGUE_KINDS)[number];
export type CatalogueStatus = "active" | "inactive" | "retired";

export interface CatalogueItemBase {
  id: string;
  code: string;
  name: string;
  status: CatalogueStatus;
  version: number;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
  updated_at: string;
}

export type ProductCatalogueItem = CatalogueItemBase;

export interface ServiceCatalogueItem extends CatalogueItemBase {
  product_id: string;
}

export interface ModuleCatalogueItem extends CatalogueItemBase {
  service_id: string;
}

export interface CampaignCatalogueItem extends CatalogueItemBase {
  module_id: string;
}

export type CatalogueItem =
  | ProductCatalogueItem
  | ServiceCatalogueItem
  | ModuleCatalogueItem
  | CampaignCatalogueItem;

export interface CataloguePage<T extends CatalogueItem = CatalogueItem> {
  items: T[];
  next_cursor: string | null;
}

export interface AdminCatalogueResponse {
  products: ProductCatalogueItem[];
  services: ServiceCatalogueItem[];
  modules: ModuleCatalogueItem[];
  campaigns: CampaignCatalogueItem[];
}

export interface CatalogueMutationResponse {
  item: CatalogueItem;
  correlation_id: string;
}

export type AccessStatus = "active" | "suspended" | "revoked";

export interface AccessAssignment {
  id: string;
  org_id: string;
  module_id: string;
  status: AccessStatus;
  effective_from: string;
  effective_to: string | null;
  version: number;
  created_at: string;
}

export interface AdminOrganisation {
  id: string;
  name: string;
  status: "active" | "suspended" | "closed";
  created_at: string;
}

export interface AdminMember {
  user_id: string;
  email: string | null;
  full_name: string | null;
  created_at: string;
}

export interface AdminModuleSubscription extends AccessAssignment {
  module_code: string;
  module_name: string;
}

export interface AdminUserModuleGrant extends AdminModuleSubscription {
  user_id: string;
  email: string | null;
  full_name: string | null;
  module_subscription_id: string;
}

export interface AdminCampaignSubscription extends AdminModuleSubscription {
  module_subscription_id: string;
  campaign_id: string;
  campaign_code: string;
  campaign_name: string;
}

export interface AdminOrganisationPage {
  organisations: AdminOrganisation[];
  next_cursor: string | null;
}

export interface AdminMemberPage {
  members: AdminMember[];
  next_cursor: string | null;
}

export interface AdminModuleSubscriptionPage {
  module_subscriptions: AdminModuleSubscription[];
  next_cursor: string | null;
}

export interface AdminUserModuleGrantPage {
  user_module_grants: AdminUserModuleGrant[];
  next_cursor: string | null;
}

export interface AdminCampaignSubscriptionPage {
  campaign_subscriptions: AdminCampaignSubscription[];
  next_cursor: string | null;
}

export interface AccessMutationResponse {
  subscription: AccessAssignment;
}

/** GET /v1/me/entitlements. The selected organisation is verified by RADAR before this reaches the browser. */
export interface ModuleEntitlement {
  product_code: string;
  service_code: string;
  module_code: string;
  campaign_codes: string[];
}

export interface MeEntitlementsResponse {
  org_id: string;
  modules: ModuleEntitlement[];
}
